import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { useSearchParams } from "react-router-dom";

import { GridStack } from "gridstack";
import type { GridItemHTMLElement, GridStackWidget } from "gridstack";

import FooterText from "src/components/footerText";
import AuthSubHeader from "src/components/authSubHeader";

import OnTherapyPatient from "src/components/patientListing/onTherapyPatient";
import OffTherapyPatient from "src/components/patientListing/offTherapyPatient";
import PendingTherapyPatient from "src/components/patientListing/pendingTherapyPatient";

import PatientFilter from "src/components/manufacturer/filters/PatientFilters";

import useFetch from "src/hooks/useFetch";

import { APIEndpoints } from "src/types/apiTypes";

import { AuthContext } from "src/context/authContext";
import { LoaderContext } from "src/context/loaderContext";

import {
  ActionDashboardActionType,
  PatientDashboardApiResponse,
  PatientFiltersState,
  PatientFilterStateActions,
} from "../../patient/types";

import { initialState, tableFilterReducer } from "src/pages/patient/reducer";

import { UserObjectType } from "src/components/authSubHeader/types";

import { getRole } from "src/helpers";

/* ============================================================
 * DEFAULT SECTION ORDER
 * ============================================================
 */

const DEFAULT_SECTION_ORDER = [
  "on_therapy",
  "off_therapy",
  "pending_therapy",
] as const;

type DashboardSectionId = (typeof DEFAULT_SECTION_ORDER)[number];

/* ============================================================
 * TYPES
 * ============================================================
 */

interface DashboardSectionPosition {
  id: DashboardSectionId;
  position: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface GridSectionProps {
  id: DashboardSectionId;
  title: ReactNode;
  children: ReactNode;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

/*
 * Extend your existing patient statistics response locally.
 *
 * You can instead add:
 *
 * section_order?: string[];
 *
 * directly to PatientDashboardApiResponse["data"].
 */
type PatientStatisticsResponse = Omit<PatientDashboardApiResponse, "data"> & {
  data: PatientDashboardApiResponse["data"] & {
    section_order?: string[] | null;
  };
};

interface SectionOrderUpdateResponse {
  data?: unknown;
  message?: string;
}

/* ============================================================
 * HELPERS
 * ============================================================
 */

const isDashboardSectionId = (value: unknown): value is DashboardSectionId =>
  typeof value === "string" &&
  DEFAULT_SECTION_ORDER.includes(value as DashboardSectionId);

/**
 * Only accept API order if all patient sections are present
 * exactly once.
 *
 * Otherwise use the default order.
 */
const resolveSectionOrder = (order?: string[] | null): DashboardSectionId[] => {
  const validOrder = order?.filter(isDashboardSectionId) ?? [];

  return validOrder.length === DEFAULT_SECTION_ORDER.length &&
    new Set(validOrder).size === DEFAULT_SECTION_ORDER.length
    ? validOrder
    : [...DEFAULT_SECTION_ORDER];
};

/**
 * Make sure GridStack still owns mounted DOM.
 */
const isGridActive = (grid: GridStack | null): grid is GridStack =>
  Boolean(grid?.el?.isConnected);

/**
 * Read current visual order from GridStack.
 */
const getSectionPositions = (grid: GridStack): DashboardSectionPosition[] => {
  if (!isGridActive(grid)) {
    return [];
  }

  try {
    return grid
      .getGridItems()
      .map((item) => {
        const id = item.dataset.gridId;

        const node = item.gridstackNode;

        if (!isDashboardSectionId(id) || !node) {
          return null;
        }

        return {
          id,
          position: 0,
          x: node.x ?? 0,
          y: node.y ?? 0,
          w: node.w ?? 1,
          h: node.h ?? 1,
        };
      })
      .filter((item): item is DashboardSectionPosition => item !== null)
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map((item, index) => ({
        ...item,
        position: index + 1,
      }));
  } catch (error) {
    return [];
  }
};

/**
 * Restore a patient section order.
 *
 * Important:
 *
 * We intentionally DO NOT restore h.
 *
 * The height depends on:
 * - patient rows
 * - pagination
 * - collapsed/expanded state
 *
 * resizeToContent() calculates that for us.
 */
const applySectionOrder = (
  grid: GridStack,
  order: DashboardSectionId[],
): void => {
  if (!isGridActive(grid)) {
    return;
  }

  const items = grid.getGridItems();

  const itemsById = new Map<DashboardSectionId, GridItemHTMLElement>();

  items.forEach((item) => {
    const id = item.dataset.gridId;

    if (isDashboardSectionId(id)) {
      itemsById.set(id, item);
    }
  });

  let y = 0;

  const layout = order.reduce<GridStackWidget[]>((widgets, id) => {
    const item = itemsById.get(id);

    if (!item) {
      return widgets;
    }

    /*
     * Used only to calculate where the next
     * widget should start.
     */
    const currentHeight = item.gridstackNode?.h ?? 1;

    widgets.push({
      id,

      x: 0,

      y,

      w: 1,

      noResize: true,

      sizeToContent: true,
    });

    y += currentHeight;

    return widgets;
  }, []);

  if (layout.length === DEFAULT_SECTION_ORDER.length) {
    /*
     * false:
     *
     * Do not add/remove React DOM.
     * Only update GridStack positions.
     */
    grid.load(layout, false);
  }
};

/* ============================================================
 * GRID SECTION
 * ============================================================
 */

const GridSection: FC<GridSectionProps> = ({
  id,
  title,
  children,
  isCollapsed,
  onToggleCollapse,
}): JSX.Element => {
  return (
    <div className="grid-stack-item" data-grid-id={id}>
      <div className="grid-stack-item-content">
        <section className="card action-dashboard-grid-card">
          {/* ===============================================
              HEADER
          =============================================== */}

          <div className="card-header custom-bg-purple d-flex align-items-center justify-content-between gap-3">
            {/* =============================================
                DRAG HANDLE
            ============================================= */}

            

            {/* =============================================
                TITLE
            ============================================= */}

            <h3 className="text-white mb-0 flex-grow-1">{title}</h3>

            {/* =============================================
                COLLAPSE
            ============================================= */}

            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn btn-link text-white p-0 border-0 action-dashboard-collapse-button"
                title={isCollapsed ? `Expand ${id}` : `Collapse ${id}`}
                aria-label={
                  isCollapsed
                    ? `Expand ${id} section`
                    : `Collapse ${id} section`
                }
                aria-expanded={!isCollapsed}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  onToggleCollapse();
                }}
              >
                <i
                  className={`fas ${
                    isCollapsed ? "fa-chevron-down" : "fa-chevron-up"
                  }`}
                  aria-hidden="true"
                />
              </button>

              <button
              type="button"
              className="action-dashboard-drag-handle"
              title={`Move ${id}`}
              aria-label={`Move ${id} section`}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <i className="fas fa-grip-vertical" aria-hidden="true" />
            </button>
            </div>
          </div>

          {/* ===============================================
              BODY
          =============================================== */}

          {!isCollapsed && <div className="card-body p-0">{children}</div>}
        </section>
      </div>
    </div>
  );
};

/* ============================================================
 * PATIENT MANUFACTURER LISTING
 * ============================================================
 */

const PatientManufacturerListing: FC = (): JSX.Element => {
  /* ========================================================
   * REDUCER / URL
   * ========================================================
   */

  const [queryParams, dispatch] = useReducer(
    tableFilterReducer,
    initialState(),
  );

  const [searchParams, updateSearchParams] = useSearchParams();

  /* ========================================================
   * COLLAPSED SECTIONS
   * ========================================================
   */

  const [collapsedSections, setCollapsedSections] = useState<
    Record<DashboardSectionId, boolean>
  >({
    on_therapy: false,
    off_therapy: false,
    pending_therapy: false,
  });

  /* ========================================================
   * GRIDSTACK REFS
   * ========================================================
   */

  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const gridInstanceRef = useRef<GridStack | null>(null);

  /*
   * requestAnimationFrame refs used when measuring
   * table height after React updates the DOM.
   */
  const refreshFrameRef = useRef<number | null>(null);

  const secondRefreshFrameRef = useRef<number | null>(null);

  /*
   * Current visual order.
   */
  const currentOrderRef = useRef<DashboardSectionId[]>([
    ...DEFAULT_SECTION_ORDER,
  ]);

  /*
   * Avoid stale callback inside the GridStack
   * dragstop event.
   */
  const saveOrderRef = useRef<(order: DashboardSectionId[]) => void>(
    () => undefined,
  );

  /*
   * Keeps track of the order currently being
   * saved.
   *
   * This prevents an older patient statistics
   * response from immediately resetting the
   * user's new drag order.
   */
  const pendingSectionOrderRef = useRef<DashboardSectionId[] | null>(null);

  /* ========================================================
   * CONTEXT
   * ========================================================
   */

  const { setLoader, hideLoader } = useContext(LoaderContext);

  const { userData } = useContext(AuthContext);

  /* ========================================================
   * REQUEST PARAMS
   * ========================================================
   */

  const {
    start_date: _startDate,
    end_date: _endDate,
    ...cleanedQueries
  } = queryParams;

  /* ========================================================
   * PATIENT STATISTICS API
   * ========================================================
   */

  const {
    loading: patientStatisticsLoading,

    data: patientStatisticsData,

    error: patientStatisticsError,

    mutate: patientStatisticsDashboardMutate,
  } = useFetch<PatientStatisticsResponse>(
    APIEndpoints.PATIENT_STATISTICS,
    cleanedQueries,
    {},
    false,
  );

  /* ========================================================
   * PATIENT SECTION ORDER API
   * ========================================================
   *
   * Add this endpoint to APIEndpoints:
   *
   * PATIENTDASHBOARD_SECTION_ORDER
   *
   * It should accept:
   *
   * {
   *   section_order: [
   *     "on_therapy",
   *     "off_therapy",
   *     "pending_therapy"
   *   ]
   * }
   *
   * ========================================================
   */

  const {
    data: sectionOrderUpdateResponse,

    error: sectionOrderUpdateError,

    mutate: updateSectionOrderData,
  } = useFetch<SectionOrderUpdateResponse>(
    APIEndpoints.ACTIONDASHBOARD_SECTION_ORDER,
    {},
    {},
    false,
  );

  /* ========================================================
   * PATIENT DATA
   * ========================================================
   */

  const onTherapy = useMemo(
    () => patientStatisticsData?.data?.on_treatment,
    [patientStatisticsData?.data?.on_treatment],
  );

  const offTherapy = useMemo(
    () => patientStatisticsData?.data?.off_treatment,
    [patientStatisticsData?.data?.off_treatment],
  );

  const pendingTherapy = useMemo(
    () => patientStatisticsData?.data?.pending_treatment,
    [patientStatisticsData?.data?.pending_treatment],
  );

  /* ========================================================
   * API SECTION ORDER
   * ========================================================
   */

  const apiSectionOrder = useMemo(
    () => resolveSectionOrder(patientStatisticsData?.data?.section_order),
    [patientStatisticsData?.data?.section_order],
  );

  /* ========================================================
   * PRACTICE / DOCTOR LISTS
   * ========================================================
   */

  const [practiceListing, setPracticeListing] = useState<
    PatientDashboardApiResponse["data"]["practice_dropdown"]
  >([]);

  const [doctorListing, setDoctorListing] = useState<
    PatientDashboardApiResponse["data"]["doctor_dropdown"]
  >([]);

  /* ========================================================
   * SAVE SECTION ORDER
   * ========================================================
   */

  const saveSectionOrder = useCallback(
    (order: DashboardSectionId[]): void => {
      /*
       * Immediately consider this the current order.
       */
      currentOrderRef.current = order;

      /*
       * Remember that this particular order
       * is currently being persisted.
       */
      pendingSectionOrderRef.current = order;

      /*
       * Same API pattern as Action Dashboard.
       */
      updateSectionOrderData({
        section_order: order,
        page: "patient",
        
      });
    },
    [updateSectionOrderData],
  );

  /*
   * Keep GridStack callback connected to latest
   * saveSectionOrder function.
   */
  useEffect(() => {
    saveOrderRef.current = saveSectionOrder;
  }, [saveSectionOrder]);

  /* ========================================================
   * INITIAL LOADER
   * ========================================================
   */

  useLayoutEffect(() => {
    setLoader();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ========================================================
   * GRID REFRESH
   * ========================================================
   *
   * Correct sequence:
   *
   * React render
   *      ↓
   * restore desired order
   *      ↓
   * browser calculates table height
   *      ↓
   * resizeToContent()
   *      ↓
   * compact
   *
   * ========================================================
   */

  const scheduleGridRefresh = useCallback((grid: GridStack): void => {
    /*
     * Cancel existing scheduled refresh.
     */
    if (refreshFrameRef.current !== null) {
      window.cancelAnimationFrame(refreshFrameRef.current);

      refreshFrameRef.current = null;
    }

    if (secondRefreshFrameRef.current !== null) {
      window.cancelAnimationFrame(secondRefreshFrameRef.current);

      secondRefreshFrameRef.current = null;
    }

    /*
     * First frame:
     * allow React DOM update.
     */
    refreshFrameRef.current = window.requestAnimationFrame(() => {
      refreshFrameRef.current = null;

      /*
       * Second frame:
       * allow tables/browser layout
       * calculation to complete.
       */
      secondRefreshFrameRef.current = window.requestAnimationFrame(() => {
        secondRefreshFrameRef.current = null;

        if (gridInstanceRef.current !== grid || !isGridActive(grid)) {
          return;
        }

        /*
         * Measure after React restores the body. The first measurement
         * gives applySectionOrder() the current expanded height.
         */
        grid.getGridItems().forEach((item) => {
          grid.resizeToContent(item);
        });

        applySectionOrder(grid, currentOrderRef.current);

        /*
         * load() can update widget geometry, so measure once more before
         * compacting the dashboard.
         */
        grid.getGridItems().forEach((item) => {
          grid.resizeToContent(item);
        });

        /*
         * Remove blank space created when
         * cards collapse or row counts change.
         */
        grid.compact("list");
      });
    });
  }, []);

  /* ========================================================
   * COLLAPSE / EXPAND
   * ========================================================
   */

  const toggleSectionCollapse = useCallback((id: DashboardSectionId): void => {
    setCollapsedSections((previous) => ({
      ...previous,

      [id]: !previous[id],
    }));
  }, []);

  /*
   * React first adds/removes card-body.
   *
   * Then GridStack measures its new actual height.
   */
  useEffect(() => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    scheduleGridRefresh(grid);
  }, [collapsedSections, scheduleGridRefresh]);

  /* ========================================================
   * PATIENT TABLE HEIGHT REFRESH
   * ========================================================
   *
   * Filtering / pagination changes table height.
   *
   * Recalculate GridStack whenever patient data changes.
   * ========================================================
   */

  useEffect(() => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    scheduleGridRefresh(grid);
  }, [onTherapy, offTherapy, pendingTherapy, scheduleGridRefresh]);

  /* ========================================================
   * INITIALIZE GRIDSTACK
   * ========================================================
   */

  useEffect(() => {
    const container = gridContainerRef.current;

    if (!container || gridInstanceRef.current) {
      return;
    }

    const initializedGrid = GridStack.init(
      {
        /*
         * Patient tables are always full width,
         * so a single-column grid is best.
         */
        column: 1,

        /*
         * Small cells provide fine-grained
         * automatic height calculation.
         */
        cellHeight: 10,

        margin: 12,

        disableResize: true,

        float: false,

        animate: true,

        sizeToContent: true,

        /*
         * Only the grip icon can start a drag.
         *
         * Pagination/table buttons remain usable.
         */
        handle: ".action-dashboard-drag-handle",

        placeholderClass: "grid-stack-placeholder",
      },

      container,
    );

    if (!initializedGrid) {
      console.error("Patient GridStack initialization failed");

      return;
    }

    const grid: GridStack = initializedGrid;

    gridInstanceRef.current = grid;

    /* ======================================================
     * CONFIGURE REACT-RENDERED ITEMS
     * ======================================================
     */

    grid.getGridItems().forEach((item) => {
      const id = item.dataset.gridId;

      if (!isDashboardSectionId(id)) {
        return;
      }

      grid.update(item, {
        id,

        x: 0,

        w: 1,

        noResize: true,

        sizeToContent: true,
      });
    });

    /* ======================================================
     * INITIAL ORDER / HEIGHT
     * ======================================================
     */

    applySectionOrder(grid, currentOrderRef.current);

    scheduleGridRefresh(grid);

    /* ======================================================
     * DRAG STOP
     * ======================================================
     */

    const handleDragStop = (
      _event: Event,
      draggedElement: GridItemHTMLElement,
    ): void => {
      if (gridInstanceRef.current !== grid || !isGridActive(grid)) {
        return;
      }

      /*
       * Read new visible positions.
       */
      const positions = getSectionPositions(grid);

      /*
       * Don't save partial/broken layout.
       */
      if (positions.length !== DEFAULT_SECTION_ORDER.length) {
        return;
      }

      /*
       * Convert GridStack positions into
       * our simple ordered ID array.
       */
      const order = positions.map(({ id }) => id);

      currentOrderRef.current = order;

      console.log("Dragged patient section:", draggedElement.dataset.gridId);

      console.log("Updated patient section order:", order);

      console.table(positions);

      /*
       * Persist section order to backend.
       */
      saveOrderRef.current(order);

      /*
       * Normalize layout after dragging.
       */
      scheduleGridRefresh(grid);
    };

    grid.on("dragstop", handleDragStop);

    /* ======================================================
     * CLEANUP
     * ======================================================
     */

    return () => {
      if (refreshFrameRef.current !== null) {
        window.cancelAnimationFrame(refreshFrameRef.current);

        refreshFrameRef.current = null;
      }

      if (secondRefreshFrameRef.current !== null) {
        window.cancelAnimationFrame(secondRefreshFrameRef.current);

        secondRefreshFrameRef.current = null;
      }

      if (gridInstanceRef.current === grid) {
        gridInstanceRef.current = null;
      }

      if (isGridActive(grid)) {
        grid.off("dragstop");

        /*
         * React owns DOM, so don't delete
         * children during cleanup.
         */
        grid.destroy(false);
      }
    };
  }, [scheduleGridRefresh]);

  /* ========================================================
   * APPLY API SECTION ORDER
   * ========================================================
   */

  useEffect(() => {
    if (!patientStatisticsData) {
      return;
    }

    const pendingOrder = pendingSectionOrderRef.current;

    const serverOrderKey = apiSectionOrder.join("|");

    const pendingOrderKey = pendingOrder?.join("|");

    /*
     * Example:
     *
     * User just dragged:
     *
     * pending, on, off
     *
     * but PATIENT_STATISTICS returns an older:
     *
     * on, off, pending
     *
     * Ignore that stale server response while
     * the new order is being persisted.
     */
    if (pendingOrder && serverOrderKey !== pendingOrderKey) {
      return;
    }

    currentOrderRef.current = apiSectionOrder;

    if (serverOrderKey === pendingOrderKey) {
      pendingSectionOrderRef.current = null;
    }

    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    /*
     * Restore saved API order.
     */
    applySectionOrder(grid, apiSectionOrder);

    /*
     * Re-measure actual patient table heights.
     */
    scheduleGridRefresh(grid);
  }, [patientStatisticsData, apiSectionOrder, scheduleGridRefresh]);

  /* ========================================================
   * SECTION ORDER SAVE RESPONSE
   * ========================================================
   */

  useEffect(() => {
    if (sectionOrderUpdateError) {
      console.error(
        "Unable to update patient dashboard section order:",
        sectionOrderUpdateError,
      );

      /*
       * Don't permanently lock API order handling
       * after an unsuccessful request.
       */
      pendingSectionOrderRef.current = null;

      return;
    }

    if (!sectionOrderUpdateResponse) {
      return;
    }

    /*
     * Save finished successfully.
     */
    pendingSectionOrderRef.current = null;
  }, [sectionOrderUpdateError, sectionOrderUpdateResponse]);

  /* ========================================================
   * PATIENT LOADER
   * ========================================================
   */

  useEffect(() => {
    if (patientStatisticsData || patientStatisticsError) {
      hideLoader();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientStatisticsData, patientStatisticsError]);

  /* ========================================================
   * PRACTICE / DOCTOR DROPDOWNS
   * ========================================================
   */

  useEffect(() => {
    const practices = patientStatisticsData?.data?.practice_dropdown;

    const doctors = patientStatisticsData?.data?.doctor_dropdown;

    if (practices && practices.length > practiceListing.length) {
      setPracticeListing(practices);
    }

    if (doctors && doctors.length > doctorListing.length) {
      setDoctorListing(doctors);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientStatisticsData]);

  /* ========================================================
   * MANUFACTURER USER CHANGE
   * ========================================================
   */

  const reTriggerPatientStatsWhenUserChange = (value: UserObjectType): void => {
    const updatedParams = {
      ...initialState(),

      role: value.role,

      id: value.id.toString(),

      ref_user: value.role !== "Manufacturer" ? value.id.toString() : "",
    };

    const {
      start_date: _updatedStartDate,

      end_date: _updatedEndDate,

      ...requestParams
    } = updatedParams;

    setLoader();

    setPracticeListing([]);

    setDoctorListing([]);

    updateSearchParams(
      new URLSearchParams({
        id: value.id.toString(),

        role: value.role,
      }),

      {
        replace: false,
      },
    );

    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,

      payload: updatedParams,
    });

    patientStatisticsDashboardMutate({}, {}, requestParams);
  };

  /* ========================================================
   * PAGE CHANGE
   * ========================================================
   */

  const onChangingPage = (action: ActionDashboardActionType): void => {
    const updatedData = tableFilterReducer(queryParams, action);

    const {
      start_date: _updatedStartDate,

      end_date: _updatedEndDate,

      ...requestParams
    } = updatedData;

    dispatch(action);

    setLoader();

    updateSearchParams(
      new URLSearchParams(requestParams as unknown as Record<string, string>),

      {
        replace: false,
      },
    );

    setPracticeListing([]);

    setDoctorListing([]);

    patientStatisticsDashboardMutate({}, {}, requestParams);
  };

  /* ========================================================
   * RESET DASHBOARD LAYOUT
   * ========================================================
   */

  const resetDashboardLayout = (): void => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    const defaultOrder: DashboardSectionId[] = [...DEFAULT_SECTION_ORDER];

    currentOrderRef.current = defaultOrder;

    applySectionOrder(grid, defaultOrder);

    scheduleGridRefresh(grid);

    /*
     * Save default ordering to backend too.
     */
    saveOrderRef.current(defaultOrder);
  };

  /* ========================================================
   * UPDATE FILTERS
   * ========================================================
   */

  const updateQueryParamsAndCallAPI = (
    updatedQueries: PatientFiltersState,

    isReset: boolean = false,
  ): void => {
    setLoader();

    /* ======================================================
     * RESET FILTERS
     * ======================================================
     */

    if (isReset) {
      const role = getRole() || userData?.ref_type || "";

      const id = userData?.id?.toString() || "";

      const defaultQueryParams = tableFilterReducer(
        queryParams,

        {
          type: PatientFilterStateActions.RESET_FILTERS,

          payload: {
            role,

            id,

            ref_user: role !== "Manufacturer" ? id : "",
          },
        },
      );

      const {
        start_date: _defaultStartDate,

        end_date: _defaultEndDate,

        ...requestParams
      } = defaultQueryParams;

      setPracticeListing([]);

      setDoctorListing([]);

      updateSearchParams(
        new URLSearchParams({
          role,

          id,

          ref_user: role !== "Manufacturer" ? id : "",
        }),

        {
          replace: false,
        },
      );

      dispatch({
        type: PatientFilterStateActions.RESET_FILTERS,

        payload: defaultQueryParams,
      });

      patientStatisticsDashboardMutate({}, {}, requestParams);

      return;
    }

    /* ======================================================
     * APPLY FILTERS
     * ======================================================
     */

    const updatedParams = {
      ...queryParams,

      case_id: updatedQueries.case_id,

      global_doctor_id: updatedQueries.global_doctor_id,

      global_patient_id: updatedQueries.global_patient_id,

      patient_name: updatedQueries.patient_name,

      practice_id: updatedQueries.practice_id,

      start_date: updatedQueries.start_date,

      end_date: updatedQueries.end_date,

      /*
       * Preserve patient section pagination.
       */
      pending_therapy_page:1,

      on_therapy_page:1,

      off_therapy_page:1,
       
      //page: 1
    };

    const {
      start_date: _updatedStartDate,

      end_date: _updatedEndDate,

      ...requestParams
    } = updatedParams;

    updateSearchParams(
      new URLSearchParams(requestParams as unknown as Record<string, string>),

      {
        replace: false,
      },
    );

    /*
     * Your previous PatientManufacturerListing
     * built updatedData but didn't dispatch it
     * in this branch.
     *
     * Keep reducer/UI/URL/API synchronized.
     */
    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,

      payload: updatedParams,
    });

    patientStatisticsDashboardMutate({}, {}, requestParams);
  };

  /* ========================================================
   * INITIALIZE FILTERS FROM URL
   * ========================================================
   */

  useEffect(() => {
    const role =
      searchParams.get("role") || getRole() || userData?.ref_type || "";

    const id = searchParams.get("id") || userData?.id?.toString() || "";

    const updatedParams = {
      ...queryParams,

      case_id: searchParams.get("case_id") || "",

      global_patient_id: searchParams.get("global_patient_id") || "",

      patient_name: searchParams.get("patient_name") || "",

      global_doctor_id: searchParams.get("global_doctor_id") || "",

      practice_id: searchParams.get("practice_id") || "",

      pending_therapy_page:
        Number(searchParams.get("pending_therapy_page")) || 1,

      on_therapy_page: Number(searchParams.get("on_therapy_page")) || 1,

      off_therapy_page: Number(searchParams.get("off_therapy_page")) || 1,

      role,

      id,

      ref_user: role !== "Manufacturer" ? id : "",
    };

    if (patientStatisticsLoading) {
      return;
    }

    const {
      start_date: _updatedStartDate,

      end_date: _updatedEndDate,

      ...requestParams
    } = updatedParams;

    setLoader();

    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,

      payload: updatedParams as PatientFiltersState,
    });

    patientStatisticsDashboardMutate({}, {}, requestParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.global_doctor_id,
    queryParams.global_patient_id,
    queryParams.patient_name,
    queryParams.practice_id,
    queryParams.case_id,
  ]);

  /* ========================================================
   * REFRESH PATIENT LISTING
   * ========================================================
   */

  const refreshPatientListing = (): void => {
    const {
      start_date: _refreshStartDate,

      end_date: _refreshEndDate,

      ...requestParams
    } = queryParams;

    patientStatisticsDashboardMutate({}, {}, requestParams);
  };

  /* ========================================================
   * RENDER SECTION
   * ========================================================
   */

  const renderSection = (id: DashboardSectionId): JSX.Element => {
    switch (id) {
      /* ====================================================
       * ON THERAPY
       * ====================================================
       */

      case "on_therapy":
        return (
          <GridSection
            key={id}
            id={id}
            title="Patients Currently on Treatment"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <OnTherapyPatient
              dataList={onTherapy?.on_therapy || []}
              totalPages={onTherapy?.page_count || 0}
              currentPage={queryParams.on_therapy_page}
              changePageNumber={onChangingPage}
            />
          </GridSection>
        );

      /* ====================================================
       * OFF THERAPY
       * ====================================================
       */

      case "off_therapy":
        return (
          <GridSection
            key={id}
            id={id}
            title="Patients Not Receiving Treatment"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <OffTherapyPatient
              dataList={offTherapy?.off_therapy || []}
              totalPages={offTherapy?.page_count || 0}
              currentPage={queryParams.off_therapy_page}
              changePageNumber={onChangingPage}
            />
          </GridSection>
        );

      /* ====================================================
       * PENDING THERAPY
       * ====================================================
       */

      case "pending_therapy":
        return (
          <GridSection
            key={id}
            id={id}
            title="Patients Pending Treatment"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <PendingTherapyPatient
              dataList={pendingTherapy?.pending_therapy || []}
              totalPages={pendingTherapy?.page_count || 0}
              currentPage={queryParams.pending_therapy_page}
              changePageNumber={onChangingPage}
              refreshListing={refreshPatientListing}
            />
          </GridSection>
        );
    }
  };

  /* ========================================================
   * RENDER
   * ========================================================
   */

  return (
    <div className="container-fluid auth-page overflow-auto flex-fill">
      {/* ====================================================
          HEADER
      ==================================================== */}

      <AuthSubHeader
        defaultUser={{
          id: searchParams.get("id") || "",

          role: searchParams.get("role") || "",
        }}
        reTriggerPatientStats={reTriggerPatientStatsWhenUserChange}
      />

      {/* ====================================================
          FILTER
      ==================================================== */}

      <div className="card mt-2">
        <div className="card-body">
          <PatientFilter
            doctorDropdown={doctorListing}
            practiceDropdown={practiceListing}
            defaultQueryParams={queryParams}
            updateQueryParams={updateQueryParamsAndCallAPI}
          />
        </div>
      </div>

      {/* ====================================================
          RESET SECTION ORDER
      ==================================================== */}

      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm action-dashboard-reset-button me-3"
          onClick={resetDashboardLayout}
        >
          <i className="fas fa-undo-alt me-2" aria-hidden="true" />
          Reset Section Order
        </button>
      </div>

      {/* ====================================================
          GRIDSTACK PATIENT SECTIONS
      ==================================================== */}

      <div className="d-flex flex-column">
        <div
          ref={gridContainerRef}
          className="grid-stack action-dashboard-grid mt-3"
        >
          {DEFAULT_SECTION_ORDER.map(renderSection)}
        </div>
      </div>

      {/* ====================================================
          FOOTER
      ==================================================== */}

      <FooterText />
    </div>
  );
};

export default PatientManufacturerListing;
